import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { collection, addDoc, getDocs, query, where, doc, deleteDoc, updateDoc, orderBy, serverTimestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './ManageOffers.css';

const ManageOffers = () => {
  const [offers, setOffers] = useState([]);
  const [title, setTitle] = useState('');
  const [threshold, setThreshold] = useState('');
  const [reward, setReward] = useState('');
  const [active, setActive] = useState(true);
  const [message, setMessage] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editOfferId, setEditOfferId] = useState(null);
  const [offerDescription, setOfferDescription] = useState('');
  const coffeeShopId = localStorage.getItem('coffee_shop_id');
  const coffeeShopName = localStorage.getItem('coffee_shop_name');

  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    if (!coffeeShopId) {
      setMessage(t('error_missing_shop'));
      setOffers([]);
      return;
    }

    try {
      const offersSnap = await getDocs(
        query(
          collection(db, 'offers'),
          where('coffee_shop_id', '==', coffeeShopId),
          orderBy('createdAt', 'asc')
        )
      );
      let fetchedOffers = offersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      fetchedOffers = fetchedOffers.filter(offer => offer.coffee_shop_id && offer.createdAt);

      setOffers(fetchedOffers);
      setMessage(fetchedOffers.length === 0 ? t('no_offers') : '');
    } catch (error) {
      if (error.code === 'failed-precondition' && error.message.includes('index')) {
        const indexLink = error.message.match(/https:\/\/[^\s]+/)?.[0] || 'Firebase Console';
        setMessage(`${t('error_missing_index')} ${indexLink}`);
      } else if (error.code === 'permission-denied') {
        setMessage(t('error_permission_denied'));
      } else {
        setMessage(`${t('error_fetching')} ${error.message}`);
      }
      setOffers([]);
    }
  };

  const addOffer = async () => {
    if (!title || !threshold || !reward || !offerDescription) {
      setMessage(t('error_missing_fields'));
      return;
    }

    try {
      if (editMode) {
        await updateDoc(doc(db, 'offers', editOfferId), {
          title,
          description: offerDescription,
          threshold: Number(threshold),
          reward: Number(reward),
          active
        });
        setMessage(t('success_updated'));
        setEditMode(false);
        setEditOfferId(null);
      } else {
        await addDoc(collection(db, 'offers'), {
          coffee_shop_id: coffeeShopId,
          coffee_shop_name: coffeeShopName,
          title,
          description: offerDescription,
          threshold: Number(threshold),
          reward: Number(reward),
          offer_type: 'coffee', // <-- default for now, you'll allow switching later
          image_url: '',        // optional for retail
          price: 0,             // optional for retail
          category: '',         // optional for retail
          active,
          createdAt: serverTimestamp()
        });
        setMessage(t('success_added'));
      }
      fetchOffers();
      clearFields();
    } catch (error) {
      setMessage(`❌ Error: ${error.message}`);
    }
  };

  const deleteOffer = async (offerId) => {
    try {
      await deleteDoc(doc(db, 'offers', offerId));
      setMessage(t('success_deleted'));
      fetchOffers();
    } catch (error) {
      setMessage(`❌ Error deleting offer: ${error.message}`);
    }
  };

  const toggleOfferStatus = async (offerId, currentStatus) => {
    try {
      await updateDoc(doc(db, 'offers', offerId), { active: !currentStatus });
      setMessage(t('status_updated'));
      fetchOffers();
    } catch (error) {
      setMessage(`❌ Error updating offer status: ${error.message}`);
    }
  };

  const startEdit = (offer) => {
    setEditMode(true);
    setEditOfferId(offer.id);
    setTitle(offer.title);
    setOfferDescription(offer.description || '');
    setThreshold(offer.threshold);
    setReward(offer.reward);
    setActive(offer.active);
  };

  const clearFields = () => {
    setTitle('');
    setThreshold('');
    setReward('');
    setOfferDescription('');
    setActive(true);
    setEditMode(false);
    setEditOfferId(null);
  };

  return (
    <>
      <div className="fixed-header">
        <div className="offer-form">
          <input type="text" placeholder={t('offer_title_placeholder')} value={title} onChange={e => setTitle(e.target.value)} />
          <input type="text" placeholder={t('offer_description_placeholder')} value={offerDescription} onChange={(e) => setOfferDescription(e.target.value)} />
          <input type="number" placeholder={t('threshold_placeholder')} value={threshold} onChange={e => setThreshold(e.target.value)} />
          <input type="number" placeholder={t('reward_placeholder')} value={reward} onChange={e => setReward(e.target.value)} />
          <label>
            {t('active_label')}
            <input type="checkbox" checked={active} onChange={() => setActive(prev => !prev)} />
          </label>
          <button className="add-offer-btn" onClick={addOffer}>{editMode ? t('update_offer') : t('add_offer')}</button>
          {editMode && <button className="cancel-btn" onClick={clearFields}>{t('cancel')}</button>}
        </div>
        <h2 className="offers-title">{t('manage_offers_title', { coffeeShopName })}</h2>
      </div>
      <div className="manage-offers-container">
        {message && <div className="notification">{message}</div>}
        <div className="existing-offers">
          <h3>{t('existing_offers')}</h3>
          {offers.length > 0 ? offers.map(offer => (
            <div key={offer.id} className={`offer-card ${offer.active ? 'active' : 'inactive'}`}>
              <h4>{t('offer')}</h4>
              <p>{t('description_buy5')}</p>
              <p><strong>{t('threshold')}:</strong> {offer.threshold}</p>
<p><strong>{t('reward')}:</strong> {offer.reward}</p>
<p><strong>{t('status')}:</strong> {offer.active ? t('active') : t('inactive')}</p>
              <button className="status-btn" onClick={() => toggleOfferStatus(offer.id, offer.active)}>
                {offer.active ? t('deactivate') : t('activate')}
              </button>
              <button className="edit-btn" onClick={() => startEdit(offer)}>{t('edit_offer')}</button>
              <button className="delete-btn" onClick={() => deleteOffer(offer.id)}>{t('delete_offer')}</button>
            </div>
          )) : <p>{t('no_offers')}</p>}
        </div>
        <button className="dashboard-btn" onClick={() => navigate('/dashboard')}>{t('dashboard_back')}</button>
      </div>
    </>
  );
};

export default ManageOffers;
